import { sendEventMetric } from "@expert/cloudwatch-metrics";
import { Flex, Space } from "@mantine/core";
import { type JSX, useEffect, useRef } from "react";

import type { VoiceTask } from "@/sdk";

import { useAnalytics } from "@/analytics";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useAgentSdk } from "@/sdk";
import { useLogger } from "@/sdk/logging";

import { isConferenceLimitReached } from "../../utils";
import {
    EndCallButton,
    HoldButton,
    LeaveCallButton,
    MuteButton,
    ToggleCallbackPanelButton,
    ToggleDialpadButton,
    ToggleTransferPanelButton,
} from "../Buttons";
import { useControlsStore } from "../controls.store";
import { hasActiveDisclosureParticipant } from "../hasActiveDisclosureParticipant";

export function AssignedCallControls({ task }: { task: VoiceTask }): JSX.Element {
    const isConference = useControlsStore((state) => state.isConference);
    const agentSdk = useAgentSdk();
    const { callbackEnabled: isCallbackEnabled } = usePartnerFeatureConfigured();
    const { dispatcher } = useAnalytics();
    const logger = useLogger({ module: "AssignedCallControls" });

    /** Disable the transfer button if the conference limit has been reached. Always show when not in a conference. */
    const transferButtonDisabled = isConference && isConferenceLimitReached(task);

    // Compliance observability: the Hold control is suppressed while a disclosure is playing. Emit once
    // when that guard engages (the disclosure-participant appears) so the near-miss is visible, rather
    // than the block being silent. The button stays disabled either way.
    const disclosurePlaying = hasActiveDisclosureParticipant(task.conferenceParticipants);
    const wasDisclosurePlayingRef = useRef(false);
    useEffect(() => {
        if (disclosurePlaying && !wasDisclosurePlayingRef.current) {
            logger.info({ partner: task.partner }, "hold_suppressed_during_disclosure");
            void sendEventMetric("HoldSuppressedDuringDisclosure", { partner: task.partner });
            void dispatcher.dispatchBusinessEvent("HoldSuppressedDuringDisclosure", { partner: task.partner });
        }
        wasDisclosurePlayingRef.current = disclosurePlaying;
    }, [disclosurePlaying, task.partner, dispatcher, logger]);

    return (
        <Flex gap={"xs"}>
            <ToggleTransferPanelButton disabled={transferButtonDisabled} />
            <ToggleDialpadButton />
            {isCallbackEnabled ? <ToggleCallbackPanelButton /> : null}
            {isConference ? null : (
                <HoldButton
                    task={task}
                    isOnHold={task.holdState.onHold}
                    onPauseCallButtonClick={() => agentSdk.holdCall(task)}
                    onResumeCallButtonClick={() => agentSdk.resumeCall(task)}
                    // Compliance guard: covers the brief render before a starting disclosure flips the
                    // call into conference mode (after which this button is null and the conference
                    // panel's hold control — also disabled — takes over).
                    isDisabled={disclosurePlaying}
                />
            )}
            <MuteButton task={task} />
            <Space />
            {isConference ? <LeaveCallButton task={task} /> : <EndCallButton task={task} />}
        </Flex>
    );
}
